import { useTranslation } from 'react-i18next';

import { Card } from '@work4all/components/lib/dataDisplay/card';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { LookUp } from '@work4all/models/lib/Classes/LookUp.entity';
import { COLUMNS_BY_LOOKUP_TYPE } from '@work4all/models/lib/lookup-field-definitions';

import {
  colorPairToHex,
  hexToSignedInt,
} from '@work4all/utils/lib/colorConversion';
import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { ControllerPlus } from '../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import { Collapse, ControlWrapper, Panel } from '../../../components';

type LookUpFormValue = PathsOf<LookUp, 2>;

export const GeneralTabPanel = () => {
  const { t } = useTranslation();
  const { register, watch, control, getValues, setValue } =
    useFormContextPlus<LookUpFormValue>();

  // Lookup type is constant for mask
  const lookupType = watch('lookupType');
  const fields = COLUMNS_BY_LOOKUP_TYPE[lookupType] || [];

  return (
    <Panel style={{ height: '100%' }}>
      <Collapse defaultOpen title={t('MASK.GENERAL')}>
        <Card>
          <ControlWrapper columns={1}>
            <LabeledInput
              {...register('name')}
              required
              label={t('COMMON.NAME')}
            />
          </ControlWrapper>
          <ControlWrapper columns={2}>
            {fields.includes('color') && (
              <LabeledInput {...register('color')} label={t('COMMON.COLOR')} />
            )}

            {fields.includes('showAs') && (
              <LabeledInput
                {...register('showAs')}
                label={t('COMMON.SHOWAS')}
              />
            )}

            {fields.includes('textValue') && (
              <LabeledInput
                {...register('textValue')}
                label={t('COMMON.GROUPING')}
              />
            )}

            {fields.includes('hexColorPair') && (
              <ControllerPlus
                control={control}
                name="hexColorPair"
                render={({ field: { value, onChange } }) => {
                  let editableColor = null;
                  if (value) {
                    editableColor = value.split(',')[0];
                  } else {
                    const colorValue = getValues('color');
                    if (colorValue) {
                      editableColor = colorPairToHex(colorValue);
                    }
                  }

                  return (
                    <LabeledInput
                      onChange={(e) => {
                        setValue(
                          'color',
                          `${hexToSignedInt(e.target.value)},0`,
                          {
                            shouldDirty: true,
                          }
                        );
                        onChange(e.target.value);
                      }}
                      value={editableColor}
                      type="color"
                      label={t('COMMON.COLOR')}
                    />
                  );
                }}
              />
            )}

            {fields.includes('groupView') && (
              <ControllerPlus
                control={control}
                name="groupView"
                render={({ field: { value, ...field } }) => (
                  <CheckboxRadioItem
                    onChange={(e) => {
                      field.onChange(e.target.checked ? -1 : 0);
                    }}
                    defaultChecked={value === -1}
                    checked={value === -1}
                    label={t('COMMON.PROJECTKIND.GROUPVIEW')}
                  />
                )}
              ></ControllerPlus>
            )}

            {fields.includes('standard') && (
              <ControllerPlus
                control={control}
                name="standard"
                render={({ field: { value, ...field } }) => (
                  <CheckboxRadioItem
                    onChange={(e) => {
                      field.onChange(e.target.checked ? -1 : 0);
                    }}
                    defaultChecked={value === -1}
                    checked={value === -1}
                    label={t('COMMON.DEFAULT')}
                  />
                )}
              ></ControllerPlus>
            )}

            {fields.includes('closedStatus') && (
              <ControllerPlus
                control={control}
                name="closedStatus"
                render={({ field: { value, ...field } }) => (
                  <CheckboxRadioItem
                    onChange={(e) => {
                      field.onChange(e.target.checked ? -1 : 0);
                    }}
                    defaultChecked={value === -1}
                    checked={value === -1}
                    label={t('COMMON.CLOSED')}
                  />
                )}
              ></ControllerPlus>
            )}
          </ControlWrapper>
        </Card>
      </Collapse>
    </Panel>
  );
};
